import { action, computed, observable } from 'mobx'
import type Cart from 'mobx/Cart'
import type { NextRouter } from 'next/router'
import type { Dispatch, SetStateAction } from 'react'

import type { Store } from 'types/StoreContext'
import type { Localization, OrderTypeFlow, FlowStep, LocalizationFlow, SavedAddress, NonSavedAddress, AddressLabel } from 'mobx/Localization/types'
import type { IAddressManager, LegacyAddress, LegacyAddressByOrderType, AddressManagerV2Dependencies } from './types'

import type _User from 'mobx/User'
import type _Home from 'mobx/Home'
import type _Infra from 'mobx/Infra'

import { CONSTANTS, ORDER_TYPES } from 'utils/constants'

export default class AddressManagerV2Store implements Localization, IAddressManager {
	// ********************
	// Props and methods from the old localization flow
	// ********************

	constructor(dependencies: AddressManagerV2Dependencies) {
		this.dependencies = dependencies
	}

	private readonly dependencies: AddressManagerV2Dependencies

	@computed get newLocalizationFlowEnabled() {
		return this.dependencies.newLocalizationFlowEnabled()
	}

	@computed get addressPopup() {
		return this.dependencies.addressManager?.addressPopup
	}

	@computed get loading(): boolean {
		return this.dependencies.addressManager?.loading || false
	}

	@computed get localizedAddress(): LegacyAddress | null {
		return this.dependencies.addressManager?.localizedAddress || null
	}

	@action setAddressPopup = (data: any) => this.dependencies.addressManager?.setAddressPopup(data)

	@action closeAddressPopup = () => this.dependencies.addressManager?.closeAddressPopup()

	@action isUserLocalized = () => this.dependencies.addressManager?.isUserLocalized() || false

	@action setFullAddress = (addressInfo: any, orderType: string | undefined, fromIFrame = false, removeStoreId = false) =>
		this.dependencies.addressManager?.setFullAddress(addressInfo, orderType, fromIFrame, removeStoreId)

	@action setMatchingStoreIdToAddressByOrderType = (storeId: string, orderType: string) =>
		this.dependencies.addressManager?.setMatchingStoreIdToAddressByOrderType(storeId, orderType)

	@action getAddressCoordinatesByOrderType = (orderType: string) => this.dependencies.addressManager?.getAddressCoordinatesByOrderType(orderType)

	@action getFormattedAddressByOrderType = (orderType: string) => this.dependencies.addressManager?.getFormattedAddressByOrderType(orderType)

	@action getValidateAddressResponseByOrderType = (orderType: string, showLoader = false) =>
		this.dependencies.addressManager?.getValidateAddressResponseByOrderType(orderType, showLoader)

	@action getFullAddressByOrderType = (orderType: string) =>
		this.dependencies.addressManager?.getFullAddressByOrderType(orderType) as LegacyAddressByOrderType

	@action getAddressWhenLocalized = () => this.dependencies.addressManager?.getAddressWhenLocalized()

	@action getLocalizedOrderType = () => this.dependencies.addressManager?.getLocalizedOrderType()

	@action getTempFormattedAddressByOrderType = (orderType: string) =>
		this.dependencies.addressManager?.getTempFormattedAddressByOrderType(orderType) as string

	@action makeLocalizedForOrderTypeAndStoreId = (wru: string, pc: string, orderType: string, storeId: string, ref: any) =>
		this.dependencies.addressManager?.makeLocalizedForOrderTypeAndStoreId(wru, pc, orderType, storeId, ref)

	@action onMenuClickWhenLocalized = (cartStore: Cart, User: typeof _User) =>
		this.dependencies.addressManager?.onMenuClickWhenLocalized(cartStore, User)

	@action handleForceLocalization = (
		storeId: string,
		User: typeof _User,
		cartStore: Cart,
		Home: typeof _Home,
		Infra: typeof _Infra,
		router: NextRouter,
		setStore: Dispatch<SetStateAction<Store>>
	) => this.dependencies.addressManager?.handleForceLocalization(storeId, User, cartStore, Home, Infra, router, setStore, this)

	@action resetLocalization = () => this.dependencies.addressManager?.resetLocalization()

	@action storeMenuUrlToLS = (menuUrl: string) => this.dependencies.addressManager?.storeMenuUrlToLS(menuUrl)

	@action clearMenuUrlCache = () => this.dependencies.addressManager?.clearMenuUrlCache()

	@action setShowLocalizedAddress = (showLocalizedAddress: boolean) =>
		this.dependencies.addressManager?.setShowLocalizedAddress(showLocalizedAddress)

	getAddressFromLocaleStorage = () => this.dependencies.addressManager?.getAddressFromLocaleStorage() as LegacyAddress

	getLocalizedStoreId = () => this.dependencies.addressManager?.getLocalizedStoreId()

	// ********************
	// Props and methods from the new localization flow
	// ********************

	@computed get actualFlow(): LocalizationFlow | null | undefined {
		return this.dependencies.localizationStore?.actualFlow
	}

	@computed get processingFlow(): LocalizationFlow | null | undefined {
		return this.dependencies.localizationStore?.processingFlow
	}

	@computed get started(): boolean | undefined {
		return this.dependencies.localizationStore?.started
	}

	currentStep: FlowStep | null = null

	@computed get orderTypeFlows(): OrderTypeFlow[] {
		return this.dependencies.localizationStore?.orderTypeFlows || []
	}

	@action matchStoreForCurrentAddress = async (continueFlowpWhenNoStoreMatched?: boolean) => {
		await this.dependencies.localizationStore?.matchStoreForCurrentAddress(continueFlowpWhenNoStoreMatched)
	}

	@action setOrderTypeFlows = (orderTypeFlows: OrderTypeFlow[]) => {}

	@observable savedAddresses: SavedAddress[] = []

	@computed get savedAddressesFetched() {
		return !!this.dependencies.localizationStore?.savedAddressesFetched
	}

	@action fetchSavedAddresses = async () => {
		await this.dependencies.localizationStore?.fetchSavedAddresses()
	}

	@action clearSavedAddresses = () => {}

	@action saveNewAddress = async (address: NonSavedAddress, label: AddressLabel, customLabel?: string) => {
		return this.dependencies.localizationStore?.saveNewAddress(address, label, customLabel)
	}

	@action removeSavedAddress = async (addressId: string) => {}

	@action saveCurrentAddress = async () => {
		return this.dependencies.localizationStore!.saveCurrentAddress()
	}

	reachedMaximumSavedAddresses = false

	@computed get alreadyLocalized(): boolean | undefined {
		return this.dependencies.localizationStore?.alreadyLocalized
	}

	@action start = () => this.dependencies.localizationStore?.start()

	@action abort = () => this.dependencies.localizationStore?.abort()

	@action end = () => {}

	@action edit = () => this.dependencies.localizationStore?.edit()

	@action nextStep = () => this.dependencies.localizationStore?.nextStep()

	@action previousStep = () => this.dependencies.localizationStore?.previousStep()

	@action goToStep = (step: FlowStep) => {}

	@action canGoToStep = (step: FlowStep) => false

	@action goToLocalizedMenu = () => this.dependencies.localizationStore?.goToLocalizedMenu()

	@action getLocalizedMenuPath = () => this.dependencies.localizationStore?.getLocalizedMenuPath()

	@action getValidateAddress = () => this.dependencies.localizationStore?.getValidateAddress()

	config = () => this.dependencies.localizationStore?.config()

	@computed get savedAddressesEnabled() {
		return !!this.dependencies.localizationStore?.savedAddressesEnabled
	}

	forceLocalizationToStore = (storeId: string) => this.dependencies.localizationStore?.forceLocalizationToStore(storeId)

	getFilteredOrderTypeFlows = () => this.dependencies.localizationStore?.getFilteredOrderTypeFlows()


	// ********************
	// Utils methods using the new localization flow data to fit with the old localization flow expected values
	// ********************

	formattedOrderTypeForSession = () => { 
		const newFlowFormattedOrderType = () => {
			if(this.processingFlow) {
				if(this.processingFlow.orderTypeFlow?.orderType === ORDER_TYPES.PICKUP) {
					return ORDER_TYPES.PEAKUP
				}
				return this.processingFlow.orderTypeFlow?.orderType
			}

			if(this.actualFlow) {
				if(this.actualFlow.orderTypeFlow?.orderType === ORDER_TYPES.PICKUP) {
					return ORDER_TYPES.PEAKUP
				}
				return this.actualFlow.orderTypeFlow?.orderType
			}

			return null
		}
		
		const oldFlowFormattedOrderType = () => {
			return this.dependencies.user?.getOrderType() === CONSTANTS.DELIVERY_METHODS.DELIVERY ? ORDER_TYPES.DELIVERY : ORDER_TYPES.PEAKUP
		}

		return newFlowFormattedOrderType() || oldFlowFormattedOrderType()
	}
}
